// Merging two edits of one shared playlist (live shared playlists, Sep 2026).
//
// A shared playlist is one document several devices edit. Each device keeps
// the version it last agreed with the server — the BASE — and when the server
// has moved on while this device was editing too, the two sets of changes are
// combined by row rather than one overwriting the other. That is why every row
// carries its own `entryId`: a merge can only match rows that have names of
// their own.
//
// The rules, per row: a row either side removed is gone; a row either side
// added is kept; a row both kept takes each of its fields (tune, note, call)
// from whichever side changed that field (the server's, when both did). The ORDER comes from the side that
// reordered, the server's when both or neither did, with the other side's new
// rows slotted in after the row they followed there. Pure.

import type { PlaylistDoc, PlaylistEntry } from './playlists';

/** What a shared playlist's editors change: its name and its rows. */
export interface PlaylistContent {
  name: string;
  entries: PlaylistEntry[];
}

export function playlistContent(doc: Pick<PlaylistDoc, 'name' | 'entries'>): PlaylistContent {
  return { name: doc.name, entries: doc.entries };
}

/** Keys sorted at every depth, so equal values serialise equally. */
function sorted(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sorted);
  if (!value || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value).sort()) {
    const inner = (value as Record<string, unknown>)[key];
    if (inner !== undefined) out[key] = sorted(inner);
  }
  return out;
}

function canonicalEntry(entry: PlaylistEntry): string {
  return JSON.stringify([
    entry.entryId,
    sorted(entry.ref),
    entry.note?.trim() ? entry.note : '',
    entry.call ? sorted(entry.call) : null,
  ]);
}

/** One string per content: equal strings, equal playlists. */
export function contentKey(content: PlaylistContent): string {
  return JSON.stringify([content.name, content.entries.map(canonicalEntry)]);
}

export function sameContent(a: PlaylistContent, b: PlaylistContent): boolean {
  return contentKey(a) === contentKey(b);
}

const byId = (entries: readonly PlaylistEntry[]) => new Map(entries.map((e) => [e.entryId, e]));

/** Did `side` move any of the rows it shares with `base`? */
function reordered(base: readonly PlaylistEntry[], side: readonly PlaylistEntry[]): boolean {
  const sideIds = new Set(side.map((e) => e.entryId));
  const baseIds = new Set(base.map((e) => e.entryId));
  const a = base.map((e) => e.entryId).filter((id) => sideIds.has(id));
  const b = side.map((e) => e.entryId).filter((id) => baseIds.has(id));
  return a.some((id, i) => id !== b[i]);
}

/** A row's fields a merge takes one by one. */
const ROW_FIELDS = ['ref', 'note', 'call'] as const;
const fieldKey = (entry: PlaylistEntry, field: (typeof ROW_FIELDS)[number]) =>
  JSON.stringify(sorted(field === 'note' ? entry.note?.trim() || undefined : entry[field]) ?? null);

/**
 * The row both sides kept, field by field: each of its tune, note and call
 * from whichever side changed it (the server's when both did). By field, not
 * by row, so a note written here and a call saved there both survive.
 */
function mergeRow(
  base: PlaylistEntry | undefined,
  local: PlaylistEntry,
  remote: PlaylistEntry,
): PlaylistEntry {
  if (!base) return remote;
  const out: PlaylistEntry = { entryId: remote.entryId, ref: remote.ref };
  for (const field of ROW_FIELDS) {
    const localChanged = fieldKey(local, field) !== fieldKey(base, field);
    const remoteChanged = fieldKey(remote, field) !== fieldKey(base, field);
    const from = localChanged && !remoteChanged ? local : remote;
    if (from[field] !== undefined) (out as unknown as Record<string, unknown>)[field] = from[field];
  }
  return out;
}

export function mergePlaylistContent(
  base: PlaylistContent,
  local: PlaylistContent,
  remote: PlaylistContent,
): PlaylistContent {
  const name = local.name !== base.name && remote.name === base.name ? local.name : remote.name;

  const baseRows = byId(base.entries);
  const localRows = byId(local.entries);
  const remoteRows = byId(remote.entries);

  // A row survives unless a side that had it in the base has let it go.
  const survives = (id: string) =>
    baseRows.has(id) ? localRows.has(id) && remoteRows.has(id) : true;

  const localFirst =
    reordered(base.entries, local.entries) && !reordered(base.entries, remote.entries);
  const primary = localFirst ? local.entries : remote.entries;
  const secondary = localFirst ? remote.entries : local.entries;

  const order: string[] = primary.map((e) => e.entryId).filter(survives);
  const placed = new Set(order);
  // The other side's new rows, each after the row it followed there and after
  // anything the primary side added in the same place. A row added after every
  // row the two sides share was appended, and goes on the end whatever the
  // order around it became: two people adding a tune both find it last.
  const secondaryIds = new Set(secondary.map((e) => e.entryId));
  const lastShared = secondary.reduce((last, e, i) => (placed.has(e.entryId) ? i : last), -1);
  let previous: string | null = null;
  secondary.forEach((entry, index) => {
    const id = entry.entryId;
    if (!placed.has(id) && !baseRows.has(id)) {
      let at =
        index > lastShared ? order.length : previous === null ? 0 : order.indexOf(previous) + 1;
      while (at < order.length && !baseRows.has(order[at]) && !secondaryIds.has(order[at])) at++;
      order.splice(at, 0, id);
      placed.add(id);
    }
    if (placed.has(id)) previous = id;
  });

  const entries = order.map((id) => {
    const l = localRows.get(id);
    const r = remoteRows.get(id);
    if (l && r) return mergeRow(baseRows.get(id), l, r);
    return (l ?? r)!;
  });
  return { name, entries };
}
